import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';

import Auth from 'modules/Auth';
import { formatValue, FormatTypeEnum, getCurrency } from 'modules/I18N';
import { PermissionsEnum } from 'consts/permissions';
import warningSvg from 'assets/icons/warning.svg';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import { InputValue } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';

import { changeDealCurrentLineData, changeFormParams } from 'store/dealManagement/reducer';
import { getCurrentLineAvailability } from 'store/dealManagement/selectors/availabilityAssets';
import { getCurrentLineStartDate } from 'store/dealManagement/selectors/getCurrentLineSchedule';
import { getDealType } from 'store/dealManagement/selectors/getDealType';

import InputNumber from 'lib/InputNumber';
import { DealType, DealStatus } from 'components/common/types/Deal.types';
import InfoMessage from 'components/common/Deal/InfoMessage/InfoMessage';
import { INFO_TYPE } from 'components/common/Deal/InfoMessage/InfoMessage.types';
import { isFutureDate } from 'utils/dateUtil';
import RequiredAsterisk from 'components/common/RequiredAsterisk';

type BudgetProps = {
  isDisabled: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const Budget = ({ isDisabled }: BudgetProps): React.ReactElement | null => {
  const dispatch = useDispatch();
  const isActionsDisabled = useSelector((state: Store) => state.dealManagement.isActionsDisabled);
  const dealType = useSelector(getDealType);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const impressions = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.impressions);
  const budget = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.budget) ?? 0;
  const availability = useSelector(getCurrentLineAvailability);
  const startDate = useSelector(getCurrentLineStartDate);
  const deliveredBudget = useSelector((state: Store) => state.dealManagement.commonDeal.bids.revenue);
  const { currencyCode, localeCode } = useSelector((state: Store) => state.publisher.configuration);
  const hasNonGuaranteedExtendedTargetEnabled = useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET);

  const { totalCost } = availability;
  const isLive = bookingStatusCode === DealStatus.LIVE;
  const isDealTypeGuaranteed = dealType === DealType.GUARANTEED;
  const isCurrentlLineFutureStartDate = startDate && isFutureDate(startDate);

  const [showBudgetMessage, setShowBudgetMessage] = useState(false);
  const [budgetInput, setBudgetInput] = useState(budget);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setShowBudgetMessage(!!(totalCost && budget && totalCost !== budget));
    setBudgetInput(budget);
  }, [budget, totalCost]);

  const changeBudget = (value: number): void => {
    dispatch(changeDealCurrentLineData({ budget: value }));
  };

  const onChange = (value: InputValue): void => {
    setBudgetInput(Number(value));

    if (!isActionsDisabled) {
      dispatch(changeFormParams({ isActionsDisabled: true }));
    }
  };

  const checkBudgetChange = async (): Promise<void> =>
    new Promise((resolve, reject) => {
      if ([DealStatus.APPROVED, DealStatus.PENDING_APPROVAL, DealStatus.REJECTED].includes(bookingStatusCode)) {
        return resolve();
      }
      if (
        isLive &&
        (isDealTypeGuaranteed || useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET)) &&
        (budgetInput ?? 0) < deliveredBudget &&
        !isCurrentlLineFutureStartDate
      ) {
        return reject(new Error('Cannot decrease the budget goal below the number of delivered budget'));
      }
      return resolve();
    });

  const onBlur = async (): Promise<void> => {
    try {
      await checkBudgetChange();
      changeBudget(budgetInput);
      dispatch(changeFormParams({ isActionsDisabled: false }));
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(String(error));
    }
  };

  const getFieldInfoMessage = (): string | null =>
    errorMessage ||
    (showBudgetMessage
      ? `Your budget target of ${formatValue(budget, FormatTypeEnum.CURRENCY, currencyCode, localeCode)} is ${
          budget < totalCost ? 'less' : 'more'
        } than the total allocated cost of ${formatValue(
          totalCost,
          FormatTypeEnum.CURRENCY,
          currencyCode,
          localeCode,
        )}. Input your delivery targets for this deal line`
      : null);

  if (
    !Auth.hasPermission(PermissionsEnum.DEAL_BUDGET_VIEW) ||
    (dealType !== DealType.GUARANTEED && !hasNonGuaranteedExtendedTargetEnabled)
  )
    return null;

  const fieldInfoMessage = getFieldInfoMessage();

  return (
    <div>
      {Auth.hasPermission(PermissionsEnum.DEAL_IMPRESSIONS_VIEW) && (
        <div className="grid grid-flow-row justify-center place-items-center mt-2 -mb-3">
          <div className="w-px h-4 bg-neutral-300" />
          <span className="body-base text-neutral-600">or</span>
          <div className="w-px h-4 bg-neutral-300" />
        </div>
      )}
      <label
        htmlFor="budget"
        className="sub-header-base text-neutral-950-opacity-60 mb-2 inline-block"
        data-test-id="budget-header"
      >
        Budget
        {isDealTypeGuaranteed ? <RequiredAsterisk /> : null}
      </label>
      <div className="relative">
        <InputNumber
          name="budget"
          value={budgetInput?.toString() || ''}
          onChange={onChange}
          onBlur={onBlur}
          icon={getCurrency(localeCode, currencyCode)}
          isDisabled={isDisabled || !!impressions}
          dataTestId="budget-input"
        />
        {fieldInfoMessage ? (
          <div className="absolute top-2 -right-8">
            <InfoMessage
              type={INFO_TYPE.TOOLTIP}
              icon={
                <SVG
                  src={warningSvg}
                  className={errorMessage ? 'fill-current text-pinkRed-700' : 'fill-current text-yellow'}
                />
              }
            >
              <div className="w-48">{fieldInfoMessage}</div>
            </InfoMessage>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Budget;
